import { API_ROUTES } from '@ts-auto/shared'
import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { MessageCircle, Send, X } from 'lucide-react'
import { useI18n } from '../i18n/I18nProvider'
import { Button } from '../ui/Button'
import './chat-widget.css'

const chatIsVerified = import.meta.env.VITE_CHAT_ENABLED === 'true'
  && import.meta.env.VITE_CHAT_VERIFIED === 'true'

export function ChatWidget() {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [response, setResponse] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'error'>('idle')
  const titleId = useId()
  const widgetRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!open) return

    if (window.matchMedia('(pointer: fine)').matches) {
      window.requestAnimationFrame(() => inputRef.current?.focus())
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      window.requestAnimationFrame(() => triggerRef.current?.focus())
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!widgetRef.current?.contains(event.target as Node)) setOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  if (!chatIsVerified) return null

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!message.trim()) return
    setStatus('sending')
    setResponse('')
    try {
      const result = await fetch(API_ROUTES.chat, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: message.trim(), history: [] }),
      })
      if (!result.ok) throw new Error('Chat request failed')
      const payload = await result.json() as { response?: string }
      setResponse(payload.response ?? '')
      setMessage('')
      setStatus('idle')
    } catch {
      setStatus('error')
    }
  }

  const close = () => {
    setOpen(false)
    window.requestAnimationFrame(() => triggerRef.current?.focus())
  }

  return (
    <div className="chat-widget" ref={widgetRef}>
      {open ? (
        <section className="chat-widget__panel" role="dialog" aria-modal="false" aria-labelledby={titleId}>
          <header className="chat-widget__header">
            <div>
              <h2 className="chat-widget__title" id={titleId}>{t('contact.chatTitle')}</h2>
              <p className="chat-widget__subtitle">{t('contact.chatSubtitle')}</p>
            </div>
            <Button className="chat-widget__close" variant="quiet" size="icon" aria-label={t('contact.chatClose')} onClick={close}>
              <X aria-hidden="true" />
            </Button>
          </header>

          <div className="chat-widget__messages" aria-live="polite">
            <div className="chat-widget__message chat-widget__message--assistant">{t('contact.chatWelcome')}</div>
            {response ? <div className="chat-widget__message chat-widget__message--assistant">{response}</div> : null}
            {status === 'error' ? <p className="chat-widget__error" role="alert">{t('contact.chatUnavailable')}</p> : null}
          </div>

          <form className="chat-widget__form" onSubmit={onSubmit}>
            <label className="visually-hidden" htmlFor="chat-question">{t('contact.chatPrompt')}</label>
            <textarea
              ref={inputRef}
              id="chat-question"
              rows={1}
              value={message}
              placeholder={t('contact.chatPrompt')}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  event.currentTarget.form?.requestSubmit()
                }
              }}
            />
            <Button className="chat-widget__send" type="submit" size="icon" loading={status === 'sending'} disabled={!message.trim()} aria-label={t('contact.chatSend')}>
              <Send aria-hidden="true" />
            </Button>
          </form>
        </section>
      ) : null}

      <Button
        ref={triggerRef}
        className="chat-widget__trigger"
        size="icon"
        aria-label={open ? t('contact.chatClose') : t('contact.chatOpen')}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? <X aria-hidden="true" /> : <MessageCircle aria-hidden="true" />}
      </Button>
    </div>
  )
}
